import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../../app/hooks";
import { PrepareAppReviewDemoRequest } from "../contracts/PrepareAppReviewDemoRequest";
import { AppReviewDemoStatus } from "../models/AppReviewDemoStatus";
import {
  fetchAppReviewDemo,
  prepareAppReviewDemo,
} from "../slice-extensions/admin-app-review-slice-extension";

interface UseAppReviewDemo {
  status: AppReviewDemoStatus | null;
  busy: boolean;
  confirmOpen: boolean;
  openConfirm: () => void;
  closeConfirm: () => void;
  prepare: (request: PrepareAppReviewDemoRequest) => Promise<void>;
}

/**
 * The back office's "Prepare App Review demo" action (PD-0053): loads the demo's status, opens the
 * confirm sheet, and runs the prepare with its success / failure toast.
 */
export function useAppReviewDemo(): UseAppReviewDemo {
  const dispatch = useAppDispatch();
  const [status, setStatus] = useState<AppReviewDemoStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchAppReviewDemo())
      .unwrap()
      .then(setStatus)
      .catch(() => setStatus(null));
  }, [dispatch]);

  const prepare = async (request: PrepareAppReviewDemoRequest): Promise<void> => {
    setBusy(true);
    try {
      const prepared = await dispatch(prepareAppReviewDemo(request)).unwrap();
      setStatus(prepared);
      setConfirmOpen(false);
      toast.success("The App Review demo is ready.");
    } catch (error) {
      toast.error((error as { message?: string })?.message || "The App Review demo could not be prepared.");
    } finally {
      setBusy(false);
    }
  };

  return {
    status,
    busy,
    confirmOpen,
    openConfirm: () => setConfirmOpen(true),
    closeConfirm: () => setConfirmOpen(false),
    prepare,
  };
}
